import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import {
  Apartment,
  Block,
  Code,
  Earth,
  ErrorOutline,
  Lock,
  NotificationWarning,
  Schedule,
  ShieldLock,
  Sync,
} from '@barracuda-internal/bds-core/dist/Icons/Core';
import GroupedKpiCard from 'guardian/comp/dashboard/GroupedKpiCard';
import MiniStatusChart from 'guardian/comp/dashboard/MiniStatusChart';
import TrendStrip from 'guardian/comp/dashboard/TrendStrip';
import AdminAccountPicker from '../adminPeek/AdminAccountPicker';
import { actions } from './globalReducer';

// Globe + three.js are heavy; defer their chunk until the admin
// dashboard mounts, instead of paying the cost on every route.
const GeoLocationSection = lazy(
  () => import('guardian/comp/dashboard/GeoLocationSection')
);

const PERIODS = [
  { days: 1, label: '24h' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
];

const formatCount = (n) => Number(n || 0).toLocaleString();

const formatLatency = (ms) => {
  if (!ms) return '0 ms';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
  return `${ms} ms`;
};

const computeDelta = (current, previous) => {
  if (previous === 0) {
    if (!current) return 0;
    return 100;
  }
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

const PeriodToggle = ({ period, onChange }) => {
  const tokens = useTokens();
  return (
    <Stack
      direction="row"
      sx={{
        gap: tokens.aliasesSpaceComponentDense,
        alignItems: 'center',
      }}
    >
      {PERIODS.map(({ days, label }) => (
        <Chip
          key={days}
          bdsType={period === days ? 'interactiveEmphasis' : 'interactiveSubtle'}
          bdsSize="medium"
          clickable
          selected={period === days}
          onClick={() => onChange(days)}
          label={label}
        />
      ))}
    </Stack>
  );
};

const AdminDashboardSkeleton = () => {
  const tokens = useTokens();
  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Stack sx={{ gap: tokens.aliasesSpaceContainerSpacious }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="rectangular" width={220} height={36} />
        </Stack>
        <Skeleton variant="rectangular" height={220} />
        <Skeleton variant="rectangular" height={420} />
        <Skeleton variant="rectangular" height={220} />
      </Stack>
    </Box>
  );
};

const AdminDashboard = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const history = useHistory();
  const overview = useSelector((s) => s.global.overview);
  const loading = useSelector((s) => s.global.overviewLoading);
  const error = useSelector((s) => s.global.overviewError);
  const period = useSelector((s) => s.global.overviewPeriod);
  const currentAccount = useSelector((s) =>
    s.adminPeek?.accountId
      ? { id: s.adminPeek.accountId, name: s.adminPeek.accountName }
      : null
  );

  useEffect(() => {
    dispatch(actions.fetchOverview(period, currentAccount?.id));
  }, [dispatch, period, currentAccount?.id]);

  const handlePeriodChange = (days) => {
    dispatch(actions.setOverviewPeriod(days));
  };

  const totals = overview?.totals || {};
  const prev = overview?.previous_totals || {};
  const buckets = overview?.buckets || [];
  const topByTraffic = overview?.top_accounts_by_traffic || [];
  const topByThreats = overview?.top_accounts_by_threats || [];
  const regions = overview?.by_region || [];
  const threatType = overview?.by_threat_type || {
    pii: 0,
    prompt_injection: 0,
    both: 0,
  };

  const scansSeries = useMemo(() => buckets.map((b) => b.scans || 0), [buckets]);
  const threatsSeries = useMemo(() => buckets.map((b) => b.threats || 0), [buckets]);
  const failedSeries = useMemo(() => buckets.map((b) => b.failed || 0), [buckets]);
  const modeSeries = useMemo(
    () => (overview?.processing_mode_buckets || []).map(
      (b) => (b.sync || 0) + (b.async || 0)
    ),
    [overview]
  );

  const accountClick = (accountId) => {
    history.push(`/global/accounts?focus=${accountId}`);
  };

  if (loading && !overview) return <AdminDashboardSkeleton />;

  const blockRate = totals.total_scans
    ? Math.round(((totals.threats || 0) / totals.total_scans) * 1000) / 10
    : 0;
  const prevBlockRate = prev.total_scans
    ? Math.round(((prev.threats || 0) / prev.total_scans) * 1000) / 10
    : 0;

  const asyncTot = (overview?.processing_mode_totals?.async || 0)
    + (overview?.processing_mode_totals?.sync || 0);
  const asyncPct = asyncTot
    ? Math.round(((overview?.processing_mode_totals?.async || 0) / asyncTot) * 1000) / 10
    : 0;

  // Group A — top-line traffic & coverage (4 tiles)
  const tilesA = [
    {
      label: 'Total scans',
      value: formatCount(totals.total_scans),
      icon: <Schedule />,
      delta: {
        value: computeDelta(totals.total_scans, prev.total_scans || 0),
        positiveIsGood: true,
      },
    },
    {
      label: 'Active accounts',
      value: formatCount(totals.active_accounts),
      icon: <Apartment />,
      delta: {
        value: computeDelta(totals.active_accounts, prev.active_accounts || 0),
        positiveIsGood: true,
      },
    },
    {
      label: 'Threats blocked',
      value: formatCount(totals.threats),
      icon: <ShieldLock />,
      accent: tokens.chartsColorSeries5Range100,
      delta: {
        value: computeDelta(totals.threats, prev.threats || 0),
        positiveIsGood: false,
      },
    },
    {
      label: 'Block rate',
      value: `${blockRate}%`,
      icon: <Block />,
      delta: {
        value: computeDelta(blockRate, prevBlockRate),
        positiveIsGood: false,
      },
    },
  ];

  // Group B — health (2 tiles)
  const tilesB = [
    {
      label: 'Failed scans',
      value: formatCount(totals.failed),
      icon: <ErrorOutline />,
      delta: {
        value: computeDelta(totals.failed, prev.failed || 0),
        positiveIsGood: false,
      },
    },
    {
      label: 'Avg latency',
      value: formatLatency(totals.avg_latency_ms),
      icon: <Schedule />,
      delta: {
        value: computeDelta(totals.avg_latency_ms, prev.avg_latency_ms || 0),
        positiveIsGood: false,
      },
    },
  ];

  // Group C — threat detail PII (2 tiles)
  const tilesC = [
    {
      label: 'PII detections',
      value: formatCount(threatType.pii),
      icon: <Lock />,
      accent: tokens.chartsColorSeries6Range100,
    },
    {
      label: 'Prompt injections',
      value: formatCount(threatType.prompt_injection),
      icon: <Code />,
      accent: tokens.chartsColorSeries5Range100,
    },
  ];

  // Group D — multi-threat & geography (2 tiles)
  const tilesD = [
    {
      label: 'Multi-threat',
      value: formatCount(threatType.both),
      icon: <NotificationWarning />,
      accent: tokens.chartsColorSeries5Range100,
    },
    {
      label: 'Top region',
      value: regions[0]?.region || '—',
      icon: <Earth />,
      accent: tokens.chartsColorSeries2Range100,
    },
  ];

  // Group E — operational shape (2 tiles)
  const tilesE = [
    {
      label: 'Async share',
      value: `${asyncPct}%`,
      icon: <Sync />,
    },
    {
      label: 'Failure rate',
      value: `${totals.failure_rate || 0}%`,
      icon: <NotificationWarning />,
    },
  ];

  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Stack sx={{ gap: tokens.aliasesSpaceContainerSpacious }}>
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: tokens.aliasesSpaceComponentDefault,
          }}
        >
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
            <Typography variant="h2">
              {currentAccount ? currentAccount.name : 'All accounts'}
            </Typography>
            <Typography variant="captionSubtle">
              {currentAccount
                ? 'Account health, usage, and threat activity'
                : 'Platform-wide health, usage, and threat activity'}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{ gap: tokens.aliasesSpaceComponentDefault, alignItems: 'center' }}
          >
            <AdminAccountPicker />
            <PeriodToggle period={period} onChange={handlePeriodChange} />
          </Stack>
        </Stack>

        {error && (
          <Typography
            variant="body1"
            sx={{ color: tokens.aliasesColorTextError }}
          >
            {error}
          </Typography>
        )}

        {/* KPI strip — 2 rows on the left (4+2 / 2+2+2 tiles) + tall sidekick on the right */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: 'repeat(6, 1fr) minmax(220px, 1.4fr)',
            },
            gridAutoRows: 'auto',
            gap: tokens.aliasesSpaceContainerDefault,
            alignItems: 'stretch',
          }}
        >
          {/* Row 1: Group A (cols 1-4) | Group B (cols 5-6) */}
          <Box sx={{ gridColumn: { lg: '1 / 5' } }}>
            <GroupedKpiCard tiles={tilesA} />
          </Box>
          <Box sx={{ gridColumn: { lg: '5 / 7' } }}>
            <GroupedKpiCard tiles={tilesB} />
          </Box>

          {/* Row 2: Group C | Group D | Group E (cols 1-2 / 3-4 / 5-6) */}
          <Box sx={{ gridColumn: { lg: '1 / 3' } }}>
            <GroupedKpiCard tiles={tilesC} />
          </Box>
          <Box sx={{ gridColumn: { lg: '3 / 5' } }}>
            <GroupedKpiCard tiles={tilesD} />
          </Box>
          <Box sx={{ gridColumn: { lg: '5 / 7' } }}>
            <GroupedKpiCard tiles={tilesE} />
          </Box>

          {/* Right sidekick: col 7, spans rows 1-2. AREA — anchors the
              eye as the hero volume chart of the strip. */}
          <Box
            sx={{
              gridColumn: { lg: '7 / 8' },
              gridRow: { lg: '1 / 3' },
              minWidth: 0,
            }}
          >
            <MiniStatusChart
              title="Scans / time"
              data={scansSeries}
              color={tokens.chartsColorSeries2Range100}
              fill={tokens.chartsColorSeries2Range20}
              height="auto"
              area
            />
          </Box>
        </Box>

        {/* Geo Location row — wide card + 2 status sparklines on the right */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '3fr 1fr' },
            gap: tokens.aliasesSpaceContainerDefault,
            alignItems: 'stretch',
          }}
        >
          <Suspense fallback={<Skeleton variant="rectangular" height={400} />}>
            <GeoLocationSection
              items={regions}
              subtitle="Where requests are originating from, across all accounts"
            />
          </Suspense>
          <Stack
            sx={{
              gap: tokens.aliasesSpaceContainerDefault,
              minWidth: 0,
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {/* Pure lines for status sidebars — they read as
                  "tracking" not "volume", matching the reference's
                  Requests Status / Blocking Status panels. */}
              <MiniStatusChart
                title="Requests status"
                data={scansSeries}
                color={tokens.chartsColorSeries2Range100}
                height="auto"
                area={false}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <MiniStatusChart
                title="Blocking status"
                data={threatsSeries}
                color={tokens.chartsColorSeries5Range100}
                height="auto"
                area={false}
              />
            </Box>
          </Stack>
        </Box>

        {/* Trend strips — alternate area / line for visual rhythm.
            Area = volume story, line = trend tracking. */}
        <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
          <TrendStrip
            title="Scan activity"
            data={scansSeries}
            total={totals.total_scans}
            color={tokens.chartsColorSeries2Range100}
            fill={tokens.chartsColorSeries2Range20}
            area
            items={topByTraffic.slice(0, 5).map((a) => ({
              key: a.name,
              value: a.count,
              accountId: a.account_id,
            }))}
            itemsTitle="Top accounts"
            onItemClick={(it) => accountClick(it.accountId)}
          />
          <TrendStrip
            title="Threats blocked"
            data={threatsSeries}
            total={totals.threats}
            color={tokens.chartsColorSeries5Range100}
            area={false}
            items={topByThreats.slice(0, 5).map((a) => ({
              key: a.name,
              value: a.count,
              accountId: a.account_id,
            }))}
            itemsTitle="Top accounts"
            onItemClick={(it) => accountClick(it.accountId)}
          />
          <TrendStrip
            title="Failed scans"
            data={failedSeries}
            total={totals.failed}
            color={tokens.aliasesColorTextStaticSubtle}
            area={false}
            items={[
              { key: 'Failure rate', value: `${totals.failure_rate || 0}%` },
              { key: 'Total scans', value: totals.total_scans || 0 },
              { key: 'Active accounts', value: totals.active_accounts || 0 },
            ]}
            itemsTitle="Summary"
          />
          <TrendStrip
            title="Processing mode"
            data={modeSeries}
            total={asyncTot}
            color={tokens.chartsColorSeries1Range100}
            fill={tokens.chartsColorSeries1Range20}
            area
            items={[
              {
                key: 'Sync',
                value: overview?.processing_mode_totals?.sync || 0,
              },
              {
                key: 'Async',
                value: overview?.processing_mode_totals?.async || 0,
              },
              {
                key: 'Async share',
                value: `${asyncPct}%`,
              },
            ]}
            itemsTitle="Mix"
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default AdminDashboard;
